import React, { useEffect, useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Plus, ArrowRight, Copy, Users, Sparkles, AlertTriangle, Target, 
  Clock, Lock, Share2, X, ChevronRight, Trash2, Eye, GitBranch, BarChart3 
} from 'lucide-react'
import { toast, Toaster } from 'sonner'
import { format } from 'date-fns'
import { nanoid } from 'nanoid'

import type { 
  Kit, Participant, Node, ClarityCard, Artifact, NodeWithChildren, 
  CalibrationGraphData, ExperimentVariant, Basis 
} from './types'
import * as api from './lib/api'
import { 
  getStableParticipantId, getDisplayNameForKit, setDisplayNameForKit, 
  buildNodeTreeFromList, computeCalibrationGraph, seedDemoFloodKit, getLocalVariant 
} from './lib/localStore'

// ---------- Creator links (prominent on every screen as required) ----------
function CreatorLinks({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${compact ? 'text-[11px]' : 'text-sm'} text-ink-light flex items-center gap-3 flex-wrap`}>
      <span className="text-ink-muted">a tool by</span>
      <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noopener noreferrer"
         className="creator-link hover:text-accent underline underline-offset-2">Abhay</a>
      <span className="text-ink-muted">·</span>
      <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noopener noreferrer"
         className="creator-link hover:text-accent underline underline-offset-2">buy me a coffee</a>
    </div>
  )
}

// ---------- High-quality hand-crafted Confidence Calibration Graph ----------
function CalibrationGraph({ data, onPointClick }: { 
  data: CalibrationGraphData; 
  onPointClick?: (nodeId: string) => void 
}) {
  const { points, summary } = data
  if (!points.length) {
    return (
      <div className="text-sm text-ink-muted py-12 text-center border border-dashed rounded">
        The calibration graph appears after the first few nodes.<br />
        It shows reported confidence against what actually happened.
      </div>
    )
  }

  const w = 720, h = 240, padX = 36, padY = 28
  const minTs = Math.min(...points.map(p => p.ts))
  const maxTs = Math.max(...points.map(p => p.ts)) || minTs + 1
  const tsRange = maxTs - minTs || 1

  const getX = (ts: number) => padX + ((ts - minTs) / tsRange) * (w - padX * 2)
  const getY = (conf: number) => h - padY - (conf / 100) * (h - padY * 2)

  // Stochastic "drift band" — visual reminder that reported confidence is noisy
  const bandPath = points.map((p, i) => {
    const x = getX(p.ts)
    const y = getY(p.reported)
    const drift = Math.max(8, (100 - p.reported) * 0.18) // wider when overconfident
    const y1 = Math.max(padY, y - drift)
    const y2 = Math.min(h - padY, y + drift * 0.7)
    return i === 0 ? `M ${x} ${y1} L ${x} ${y2}` : `L ${x} ${y1} M ${x} ${y2}`
  }).join(' ')

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="calibration-svg w-full" style={{ maxWidth: '100%', height: h }}>
        <defs>
          <linearGradient id="confGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2f5d5d" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#2f5d5d" stopOpacity="0.15" />
          </linearGradient>
          <pattern id="grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M 28 0 L 0 0 0 28" fill="none" stroke="#d4d0c6" strokeWidth="0.6" />
          </pattern>
        </defs>

        {/* background */}
        <rect x="0" y="0" width={w} height={h} fill="url(#grid)" opacity="0.6" />

        {/* 50% and 80% reference lines (epistemic humility cues) */}
        <line x1={padX} y1={getY(50)} x2={w - padX} y2={getY(50)} stroke="#d4d0c6" strokeDasharray="2 3" />
        <line x1={padX} y1={getY(80)} x2={w - padX} y2={getY(80)} stroke="#d4d0c6" strokeDasharray="2 3" />
        <text x={padX + 4} y={getY(50) - 4} fontSize="9" fill="#6b7369">50%</text>
        <text x={padX + 4} y={getY(80) - 4} fontSize="9" fill="#6b7369">80%</text>

        {/* stochastic drift band (the "we are usually this noisy" visual) */}
        <path d={bandPath} stroke="#b36b2e" strokeWidth="10" strokeLinecap="round" className="stochastic-band" />

        {/* main confidence line */}
        <path 
          d={points.map((p, i) => {
            const x = getX(p.ts); const y = getY(p.reported)
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
          }).join(' ')} 
          fill="none" stroke="#2f5d5d" strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" 
        />

        {/* points */}
        {points.map((p, i) => {
          const x = getX(p.ts)
          const y = getY(p.reported)
          const isAbandoned = p.status === 'abandoned'
          const color = isAbandoned ? '#b36b2e' : (p.realized != null && Math.abs(p.reported - (p.realized || 0)) > 18 ? '#b36b2e' : '#2f5d5d')
          return (
            <g key={i}>
              <circle 
                cx={x} cy={y} r={isAbandoned ? 4.5 : 3.5} 
                fill={color} className="graph-point cursor-pointer" 
                onClick={() => onPointClick?.(p.node_id)}
              />
              {isAbandoned && (
                <circle cx={x} cy={y} r="7" fill="none" stroke="#b36b2e" strokeWidth="1.5" opacity="0.5" />
              )}
            </g>
          )
        })}

        {/* x axis labels (first + last) */}
        <text x={padX} y={h - 6} fontSize="9" fill="#6b7369">{format(new Date(points[0].ts), 'HH:mm')}</text>
        <text x={w - padX - 32} y={h - 6} fontSize="9" fill="#6b7369" textAnchor="end">
          {format(new Date(points[points.length - 1].ts), 'HH:mm')}
        </text>
      </svg>

      {/* summary strip */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-ink-light">
        <div><span className="text-ink-muted">Mean reported confidence</span> <span className="font-medium text-ink">{summary.mean_confidence}%</span></div>
        <div><span className="text-ink-muted">Abandonments logged</span> <span className="font-medium text-ink">{summary.abandonments}</span></div>
        <div><span className="text-ink-muted">Recovery clarity cards</span> <span className="font-medium text-ink">{summary.clarity_cards}</span></div>
        <div className="text-[10px] text-ink-muted">Amber rings = abandonment. Wider band = typical over/under-confidence drift.</div>
      </div>
    </div>
  )
}

// ---------- The serious Abandon + Recovery Clarity modal ----------
function AbandonClarityModal({ 
  node, open, onClose, onConfirm, participantId, variant 
}: { 
  node: Node | null; 
  open: boolean; 
  onClose: () => void; 
  onConfirm: (reason: string, clarity: { what: string; assumption?: string; surprise: number }) => void;
  participantId: string;
  variant: ExperimentVariant;
}) {
  const [reason, setReason] = useState('')
  const [whatClear, setWhatClear] = useState('')
  const [assumption, setAssumption] = useState('')
  const [surprise, setSurprise] = useState(3)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open && node) {
      setReason(node.abandoned_reason || '')
      setWhatClear('')
      setAssumption('')
      setSurprise(3)
    }
  }, [open, node])

  if (!node) return null

  const handleConfirm = async () => {
    if (!reason.trim() || !whatClear.trim()) {
      toast.error('Please describe why you abandoned it and what became clear.')
      return
    }
    setSubmitting(true)
    try {
      await onConfirm(reason.trim(), {
        what: whatClear.trim(),
        assumption: assumption.trim() || undefined,
        surprise
      })
      toast.success('Abandonment + recovery clarity recorded. This is valuable data.')
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" onClick={onClose}>
          <motion.div 
            initial={{ opacity: 0, y: 12, scale: 0.985 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={{ opacity: 0, y: 8 }}
            className="abandon-modal bg-white w-full max-w-[620px] rounded border p-6" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="mt-1"><AlertTriangle className="w-5 h-5 text-warning-amber" /></div>
              <div className="flex-1">
                <div className="font-medium tracking-tight text-lg">Log abandonment + what became clear</div>
                <div className="text-sm text-ink-muted mt-1">
                  Abandonment is not failure. It is one of the most important signals in the kit. 
                  We are explicitly capturing the moment attention was withdrawn and the clarity that followed.
                </div>
              </div>
            </div>

            <div className="mt-5 text-sm border-l-2 border-warning-amber pl-3 text-ink-light">
              Task: <span className="text-ink font-medium">{node.description}</span><br />
              Your last reported confidence: <span className="font-semibold tabular-nums">{node.confidence}%</span>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <div className="field-label mb-1.5">Why did we stop or step away from this?</div>
                <textarea 
                  className="textarea w-full h-20 p-3 text-sm" 
                  placeholder="The water at the sycamore dip was already 11 inches and rising. Foot access gone before we could reach it safely."
                  value={reason} 
                  onChange={e => setReason(e.target.value)} 
                />
              </div>

              <div>
                <div className="field-label mb-1.5">What is now clearer that was not clear when we started?</div>
                <textarea 
                  className="textarea w-full h-[92px] p-3 text-sm" 
                  placeholder="Personal vehicles and boots stop being viable once water crosses ~9 inches at that low point. We need external high-clearance or a small raft for the lowest houses."
                  value={whatClear} 
                  onChange={e => setWhatClear(e.target.value)} 
                />
              </div>

              <div>
                <div className="field-label mb-1.5">What assumption turned out to be false? (optional but powerful)</div>
                <input 
                  className="input w-full p-2.5 text-sm" 
                  value={assumption} 
                  onChange={e => setAssumption(e.target.value)}
                  placeholder="We assumed we could always walk or drive the block once we were on site."
                />
              </div>

              <div>
                <div className="field-label mb-1.5">How surprising was this clarity? (1 = we kind of knew, 5 = completely blindsided)</div>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(n => (
                    <button 
                      key={n} 
                      onClick={() => setSurprise(n)}
                      className={`px-3 py-1 text-sm rounded border ${surprise === n ? 'bg-accent text-white border-accent' : 'border-border hover:border-accent'}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={handleConfirm} disabled={submitting} className="button button-primary flex-1 py-2.5 text-sm">
                {submitting ? 'Recording...' : 'Record abandonment + clarity'}
              </button>
              <button onClick={onClose} className="button px-6 py-2.5 text-sm border">Cancel</button>
            </div>

            <div className="mt-4 text-[11px] text-ink-muted">
              This will appear on the calibration graph (amber marker) and can be forged into a shareable artifact later if you choose.
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

// ---------- Node card with all the required instruments ----------
function NodeCard({ 
  node, onUpdate, onDecompose, onAbandon, onProbe, onForge, onLogObservation, depth = 0 
}: {
  node: NodeWithChildren
  onUpdate: (id: string, patch: Partial<Node>) => void
  onDecompose: (parent: Node) => void
  onAbandon: (node: Node) => void
  onProbe: (node: Node) => void
  onForge: (node: Node) => void
  onLogObservation: (node: Node) => void
  depth?: number
}) {
  const [editing, setEditing] = useState(false)
  const [desc, setDesc] = useState(node.description)
  const [conf, setConf] = useState(node.confidence)
  const [basis, setBasis] = useState(node.confidence_basis as string)
  const [burden, setBurden] = useState(node.burden_actual ?? node.burden_expected ?? 3)

  const isAbandoned = node.status === 'abandoned'

  const saveConfidence = (newConf: number) => {
    setConf(newConf)
    onUpdate(node.id, { confidence: newConf, confidence_basis: basis as any })
  }

  return (
    <div className={`node-card ml-${Math.min(depth * 5, 12)} border border-border bg-node-bg rounded p-3.5 mb-2.5`} style={{ marginLeft: depth * 14 }}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${isAbandoned ? 'bg-warning-amber' : node.status === 'done' ? 'bg-calm-sage' : 'bg-accent'}`} />
        </div>

        <div className="flex-1 min-w-0">
          {editing ? (
            <input 
              className="input w-full p-2 text-sm" 
              value={desc} 
              onChange={e => setDesc(e.target.value)}
              onBlur={() => { onUpdate(node.id, { description: desc }); setEditing(false) }}
              onKeyDown={e => { if (e.key === 'Enter') { onUpdate(node.id, { description: desc }); setEditing(false) } }}
              autoFocus
            />
          ) : (
            <div 
              className={`text-[14.5px] leading-snug ${isAbandoned ? 'line-through opacity-60' : ''}`}
              onClick={() => setEditing(true)}
            >
              {node.description}
            </div>
          )}

          {/* Confidence + Basis + Burden row */}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted">confidence</span>
              <div className="confidence-pill text-lg tabular-nums font-semibold text-accent">{conf}</div>
              <input 
                type="range" min="10" max="100" step="5" 
                value={conf} 
                onChange={e => saveConfidence(parseInt(e.target.value))}
                className="w-24 accent-[#2f5d5d]"
              />
            </div>

            <div>
              <select 
                value={basis} 
                onChange={e => { setBasis(e.target.value); onUpdate(node.id, { confidence_basis: e.target.value as Basis }) }}
                className="text-xs bg-white border border-border rounded px-1.5 py-0.5"
              >
                <option value="guess">guess</option>
                <option value="rumor">rumor</option>
                <option value="observed">observed</option>
                <option value="group_knowledge">group knowledge</option>
                <option value="calculation">calculation</option>
                <option value="other">other</option>
              </select>
            </div>

            <div className="flex items-center gap-1 text-xs">
              <span className="text-ink-muted">burden</span>
              {[1,2,3,4,5].map(b => (
                <button key={b} onClick={() => { setBurden(b); onUpdate(node.id, { burden_actual: b }) }}
                  className={`w-5 h-5 rounded text-[10px] border ${burden >= b ? 'bg-accent text-white border-accent' : 'border-border'}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>

          {/* Status + actions */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs">
            <div className="pill">{node.status}</div>
            {node.burden_actual && <div className="pill">actual burden {node.burden_actual}</div>}
            {isAbandoned && node.abandoned_reason && (
              <div className="text-[11px] text-warning-amber max-w-[42ch] truncate">abandoned: {node.abandoned_reason}</div>
            )}

            <div className="flex-1" />

            <button onClick={() => onDecompose(node)} className="button px-2.5 py-0.5 text-xs flex items-center gap-1">
              <GitBranch className="w-3 h-3" /> decompose
            </button>
            <button onClick={() => onProbe(node)} className="button px-2.5 py-0.5 text-xs flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> probe
            </button>
            <button onClick={() => onLogObservation(node)} className="button px-2.5 py-0.5 text-xs">log observation</button>
            <button onClick={() => onForge(node)} className="button px-2.5 py-0.5 text-xs flex items-center gap-1 border-accent/60">
              <Share2 className="w-3 h-3" /> forge share
            </button>
            {!isAbandoned && (
              <button onClick={() => onAbandon(node)} className="button px-2 py-0.5 text-xs text-warning-amber border-warning-amber/40">
                abandon
              </button>
            )}
          </div>

          {/* children */}
          {node.children.length > 0 && (
            <div className="mt-2.5 pl-1 border-l border-border/70">
              {node.children.map(child => (
                <NodeCard 
                  key={child.id} 
                  node={child} 
                  onUpdate={onUpdate} 
                  onDecompose={onDecompose} 
                  onAbandon={onAbandon} 
                  onProbe={onProbe} 
                  onForge={onForge} 
                  onLogObservation={onLogObservation}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------- Main App ----------
export default function Aether() {
  const [view, setView] = useState<'landing' | 'workspace' | 'graph' | 'shares'>('landing')
  const [kit, setKit] = useState<Kit | null>(null)
  const [participant, setParticipant] = useState<Participant | null>(null)
  const [nodes, setNodes] = useState<Node[]>([])
  const [tree, setTree] = useState<NodeWithChildren[]>([])
  const [clarity, setClarity] = useState<ClarityCard[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [graph, setGraph] = useState<CalibrationGraphData | null>(null)
  const [variant, setVariant] = useState<ExperimentVariant | null>(null)

  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', context: '', horizon: '', display_name: '' })
  const [joinCode, setJoinCode] = useState('')

  const [decomposeFor, setDecomposeFor] = useState<Node | null>(null)
  const [decomposeText, setDecomposeText] = useState('')
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([])

  const [probeFor, setProbeFor] = useState<Node | null>(null)
  const [probes, setProbes] = useState<string[]>([])

  const [abandonFor, setAbandonFor] = useState<Node | null>(null)

  const [forgeFor, setForgeFor] = useState<any>(null) // node | clarity | graph | kit
  const [forgeTitle, setForgeTitle] = useState('')

  const pid = useMemo(() => getStableParticipantId(), [])

  // Load from URL or current local kit on mount
  useEffect(() => {
    const path = window.location.pathname
    if (path.startsWith('/a/')) {
      const code = path.split('/a/')[1]?.split('?')[0]
      if (code) {
        // Will be handled by client-side artifact view below
        setView('shares')
        loadPublicArtifact(code)
      }
      return
    }
    const current = localStorage.getItem('aether:currentKit')
    if (current) {
      loadKit(current).catch(() => {})
    }
  }, [])

  async function loadKit(kitIdOrCode: string) {
    setLoading(true)
    try {
      const k = await api.getKit(kitIdOrCode)
      if (!k) throw new Error('not found')
      const [ns, cl, arts, gr, v] = await Promise.all([
        api.listNodes(k.id),
        api.listClarity(k.id),
        api.listArtifacts(k.id),
        api.getCalibrationGraph(k.id),
        api.getVariant(k.id)
      ])
      setKit(k)
      setNodes(ns)
      setClarity(cl)
      setArtifacts(arts)
      setGraph(gr)
      setVariant(v)
      setTree(buildNodeTreeFromList(ns))
      setParticipant({ id: pid, kit_id: k.id, display_name: getDisplayNameForKit(k.id), joined_at: Date.now(), last_seen: Date.now() } as any)
      setView('workspace')
      localStorage.setItem('aether:currentKit', k.id)
    } catch (e) {
      toast.error('Could not load that kit on this device')
    } finally { setLoading(false) }
  }

  // (using the pure list version from localStore for both local and remote data)

  async function handleCreate() {
    if (!createForm.context.trim()) {
      toast.error('Describe the situation briefly — this seeds better decompositions.')
      return
    }
    setLoading(true)
    try {
      const res = await api.createKit({
        name: createForm.name,
        context: createForm.context,
        horizon: createForm.horizon,
        display_name: createForm.display_name || 'you'
      })
      setKit(res.kit)
      setParticipant(res.participant)
      setNodes([])
      setClarity([])
      setArtifacts([])
      setGraph(null)
      setVariant(await api.getVariant(res.kit.id))
      setTree([])
      setView('workspace')
      localStorage.setItem('aether:currentKit', res.kit.id)
      setShowCreate(false)
      setCreateForm({ name: '', context: '', horizon: '', display_name: '' })
      toast.success('Kit opened. Start by adding the first concrete observation or commitment.')
      api.emitTelemetry({ type: 'kit_opened', kit_id: res.kit.id, participant_id: res.participant.id, payload: {} })
    } catch (e: any) {
      toast.error('Create failed: ' + (e.message || ''))
    } finally { setLoading(false) }
  }

  async function handleJoin() {
    if (!joinCode.trim()) return
    setLoading(true)
    try {
      const res = await api.joinKit(joinCode.trim())
      setKit(res.kit)
      setParticipant(res.participant)
      await loadKit(res.kit.id)
      setJoinCode('')
      toast.success('Joined kit.')
    } catch (e: any) {
      toast.error(e.message || 'Join failed')
    } finally { setLoading(false) }
  }

  // Load the exact canonical demo narrative
  async function loadDemo() {
    const seeded = seedDemoFloodKit()
    const k = seeded.kit
    setKit(k)
    setParticipant({ id: pid, kit_id: k.id, display_name: getDisplayNameForKit(k.id), joined_at: Date.now(), last_seen: Date.now() } as any)
    setNodes(seeded.nodes)
    setClarity(seeded.clarity)
    setArtifacts([])
    const g = computeCalibrationGraph(k.id)
    setGraph(g)
    setVariant(getLocalVariant())
    setTree(buildNodeTreeFromList(seeded.nodes))
    setView('workspace')
    localStorage.setItem('aether:currentKit', k.id)
    toast.success('Demo kit loaded: Riverbend flash flood scenario. Explore the graph and the abandoned node.')
    api.emitTelemetry({ type: 'demo_loaded', kit_id: k.id, payload: { scenario: 'riverbend_flood' } })
  }

  async function refreshData(kitId: string) {
    const [ns, cl, gr] = await Promise.all([
      api.listNodes(kitId), api.listClarity(kitId), api.getCalibrationGraph(kitId)
    ])
    setNodes(ns)
    setClarity(cl)
    setGraph(gr)
    setTree(buildNodeTreeFromList(ns))
  }

  // Core actions
  async function addRootNode() {
    if (!kit) return
    const desc = prompt('First concrete observation or commitment (be specific and observable):')
    if (!desc?.trim()) return
    const n = await api.createNode(kit.id, {
      description: desc.trim(),
      created_by: pid,
      confidence: 60,
      confidence_basis: 'guess',
      burden_expected: 3
    })
    setNodes(prev => [...prev, n])
    setTree(buildNodeTreeFromList([...nodes, n]))
    await refreshData(kit.id)
    api.emitTelemetry({ type: 'node_created', kit_id: kit.id, participant_id: pid, node_id: n.id, payload: {} })
  }

  async function updateNode(id: string, patch: Partial<Node>) {
    if (!kit) return
    const updated = await api.updateNode(kit.id, id, { ...patch, participant_id: pid })
    if (updated) {
      setNodes(prev => prev.map(n => n.id === id ? { ...n, ...updated } : n))
      await refreshData(kit.id)
    }
  }

  function openDecompose(parent: Node) {
    setDecomposeFor(parent)
    setDecomposeText('')
    setAiSuggestions([])
  }

  async function runDecompose() {
    if (!kit || !decomposeFor) return
    const suggestions = await api.aiDecompose(kit.id, decomposeFor.description + ' ' + (decomposeText || ''))
    setAiSuggestions(suggestions)
  }

  async function acceptSuggestion(s: any) {
    if (!kit || !decomposeFor) return
    const n = await api.createNode(kit.id, {
      description: s.description,
      parent_id: decomposeFor.id,
      confidence: s.starterConfidence,
      confidence_basis: s.basis,
      burden_expected: s.burden,
      created_by: pid
    })
    setNodes(prev => [...prev, n])
    await refreshData(kit.id)
    setDecomposeFor(null)
    setAiSuggestions([])
    setDecomposeText('')
    toast.success('Micro-task added under parent. Adjust confidence and burden as you learn.')
  }

  async function openProbe(node: Node) {
    if (!kit) return
    setProbeFor(node)
    const p = await api.aiProbe(node.description, kit.context)
    setProbes(p)
  }

  function openAbandon(node: Node) {
    setAbandonFor(node)
  }

  async function confirmAbandon(reason: string, clarityData: { what: string; assumption?: string; surprise: number }) {
    if (!kit || !abandonFor) return
    await api.abandonNode(kit.id, abandonFor.id, reason, pid)
    const card = await api.createClarity(kit.id, {
      node_id: abandonFor.id,
      participant_id: pid,
      prompt_variant: variant?.probing_style || 'socratic',
      situation: abandonFor.description,
      what_became_clear: clarityData.what,
      assumption_falsified: clarityData.assumption || null,
      surprise_level: clarityData.surprise
    })
    setClarity(prev => [card, ...prev])
    await refreshData(kit.id)
    setAbandonFor(null)
    // auto-open graph after abandonment + clarity — the point of the instrument
    setView('graph')
  }

  async function forgeArtifact(target: any, type: Artifact['artifact_type']) {
    if (!kit) return
    let title = ''
    let snapshot: any = {}
    if (type === 'node') {
      title = `Node: ${target.description.slice(0, 60)}`
      snapshot = { node: target, clarity: clarity.filter(c => c.node_id === target.id) }
    } else if (type === 'clarity_card') {
      title = `Clarity: ${target.what_became_clear.slice(0, 55)}`
      snapshot = target
    } else if (type === 'graph_slice') {
      title = `Calibration graph — ${kit.name}`
      snapshot = { graph: graph, kit_name: kit.name, captured_at: Date.now() }
    } else {
      title = `Kit summary — ${kit.name}`
      snapshot = { kit, node_count: nodes.length, abandonments: graph?.summary.abandonments, clarity_count: clarity.length }
    }
    const art = await api.forgeArtifact(kit.id, pid, type, title, snapshot)
    setArtifacts(prev => [art, ...prev])
    setForgeFor(null)
    setForgeTitle('')
    // Give the user the actual shareable link
    const url = `${location.origin}/a/${art.share_code}`
    await navigator.clipboard.writeText(url)
    toast.success(`Artifact forged. Link copied: ${art.share_code}. Only this snapshot is shareable.`)
    api.emitTelemetry({ type: 'artifact_forged', kit_id: kit.id, participant_id: pid, payload: { type, code: art.share_code } })
  }

  async function loadPublicArtifact(code: string) {
    const art = await api.getPublicArtifact(code)
    if (art) {
      // render minimal beautiful view in place
      setForgeFor(art) // reuse the modal area for viewing
    } else {
      toast.error('Artifact not found or was revoked.')
    }
  }

  // Keyboard power-user niceties (field friendly)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === 'n' && view === 'workspace' && kit) {
        e.preventDefault()
        addRootNode()
      }
      if (e.key.toLowerCase() === 'g' && kit) {
        setView(v => v === 'graph' ? 'workspace' : 'graph')
      }
      if (e.key === 'Escape') {
        setDecomposeFor(null); setProbeFor(null); setAbandonFor(null); setForgeFor(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [view, kit])

  // ---------- LANDING ----------
  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-24">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-7xl font-semibold tracking-[-3.2px] serif">aether</div>
              <div className="mt-1 text-2xl text-ink-muted tracking-[-0.6px]">cognitive first-aid kit</div>
            </div>
            <CreatorLinks />
          </div>

          <div className="mt-10 max-w-2xl text-[15px] leading-relaxed text-ink-light space-y-4">
            <p>In civic emergencies, attention scatters before anything else fails. Checklists assume we already know what matters. Most tools add more noise.</p>
            <p><span className="text-ink">AETHER</span> is a small, private instrument for local resilience groups. It forces explicit task decomposition, records the real burden of each micro-commitment, treats abandonment as data, and makes recovery clarity a first-class act. The output is a living <span className="text-ink">confidence calibration graph</span> — not a plan, but a mirror.</p>
            <p className="text-xs text-ink-muted">Everything stays private on your device until you deliberately forge a shareable artifact. No accounts. No feeds. No static libraries. Only what you choose to hand to someone else.</p>
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => setShowCreate(true)} className="button button-primary px-6 py-2.5 text-sm flex items-center gap-2">
              Open a new kit <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={loadDemo} className="button px-5 py-2.5 text-sm border flex items-center gap-2">
              Load the Riverbend flood demo
            </button>
            <button onClick={() => setView('graph')} className="button px-5 py-2.5 text-sm border flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> See an example graph
            </button>
          </div>

          <div className="mt-6 flex gap-2 text-sm">
            <input 
              className="input flex-1 max-w-[210px] px-3 py-2 text-sm" 
              placeholder="JOIN CODE (e.g. RIVER4)" 
              value={joinCode} 
              onChange={e => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleJoin()}
            />
            <button onClick={handleJoin} className="button px-5">Join existing kit</button>
          </div>

          <div className="mt-16 border-t border-border pt-8 text-sm max-w-prose text-ink-light">
            <div className="uppercase tracking-[1px] text-[10px] mb-2 text-ink-muted">What this instrument captures</div>
            <ul className="space-y-1 text-[13.5px]">
              <li><strong>Task decomposition</strong> — every commitment broken into observable micro-actions with time and actor in mind.</li>
              <li><strong>Perceived burden</strong> — expected vs actual attention cost (1–5). The hidden tax of coordination under stress.</li>
              <li><strong>Abandonment point</strong> — the exact moment and reason a subtask was dropped, without shame.</li>
              <li><strong>Recovery clarity</strong> — what became newly true because we abandoned or acted. The real epistemic yield.</li>
            </ul>
          </div>

          <div className="mt-10 text-[11px] text-ink-muted max-w-prose">
            Built for Cloudflare free tier (Workers, Pages, D1, Vectorize, Workers AI + AI Gateway, Analytics Engine, KV). 
            Designed to remain usable when networks are failing.
          </div>

          <div className="mt-12"><CreatorLinks /></div>
        </div>

        <AnimatePresence>
          {showCreate && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-lg rounded border p-6" onClick={e => e.stopPropagation()}>
                <div className="font-medium text-lg tracking-tight">Open a cognitive first-aid kit</div>
                <div className="text-sm text-ink-muted mt-1">One situation. One group. Private until someone chooses to share a specific slice.</div>

                <input className="input w-full mt-5 px-3 py-2 text-sm" placeholder="Short name (optional)" value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} />
                <textarea className="textarea w-full mt-3 h-28 p-3 text-sm" placeholder="What is happening right now? Be specific: location, hazard, known constraints, time pressure." value={createForm.context} onChange={e => setCreateForm({ ...createForm, context: e.target.value })} />
                <input className="input w-full mt-3 px-3 py-2 text-sm" placeholder="Rough horizon (e.g. next 6–18 hours)" value={createForm.horizon} onChange={e => setCreateForm({ ...createForm, horizon: e.target.value })} />
                <input className="input w-full mt-3 px-3 py-2 text-sm" placeholder="Your name in this kit (optional, local only)" value={createForm.display_name} onChange={e => setCreateForm({ ...createForm, display_name: e.target.value })} />

                <div className="mt-4 flex gap-3">
                  <button onClick={handleCreate} disabled={loading} className="button button-primary px-5 py-2 text-sm flex-1">Open the kit</button>
                  <button onClick={() => setShowCreate(false)} className="button px-5 py-2 text-sm border">Cancel</button>
                </div>
                <div className="mt-3 text-[11px] text-ink-muted">Your data never leaves this device until you explicitly forge an artifact. No login. No tracking across kits.</div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <Toaster position="top-center" richColors closeButton />
      </div>
    )
  }

  // ---------- PUBLIC ARTIFACT VIEW (standalone, read-only) ----------
  if (view === 'shares' && forgeFor && (forgeFor as any).payload) {
    const a = forgeFor as any
    return (
      <div className="min-h-screen bg-paper p-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs text-ink-muted">AETHER • explicitly shared • only this slice left the original kit</div>
          <div className="mt-2 text-2xl tracking-tight">{a.artifact?.title || 'Shared artifact'}</div>
          <div className="mt-6 card p-6 text-sm whitespace-pre-wrap font-mono bg-white border">{JSON.stringify(a.payload, null, 2)}</div>
          <div className="mt-6 text-xs text-ink-muted">Nothing else from the group’s attention map is visible. This link can be revoked by its creator.</div>
          <button onClick={() => { setForgeFor(null); setView('landing') }} className="mt-8 text-sm underline">← back to aether</button>
          <div className="mt-12"><CreatorLinks compact /></div>
        </div>
      </div>
    )
  }

  if (!kit) {
    return <div className="p-8">Loading kit… <button onClick={() => setView('landing')} className="underline ml-2">start over</button></div>
  }

  const displayName = participant?.display_name || getDisplayNameForKit(kit.id)

  // ---------- WORKSPACE + GRAPH ----------
  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      {/* Top bar */}
      <div className="border-b border-border bg-white/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <button onClick={() => setView('landing')} className="font-semibold tracking-[-0.5px] serif text-xl">aether</button>
            <div className="text-[10px] px-2 py-px rounded bg-[#f0ede5] text-ink-muted font-mono">{kit.code}</div>
            <div className="text-ink-light truncate max-w-[38ch]">{kit.name}</div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {variant && <div className="pill">variant: {variant.probing_style} • {variant.confidence_verb}</div>}
            <div className="flex items-center gap-1.5 text-ink-muted"><Users className="w-3.5 h-3.5" /> {displayName}</div>
            <CreatorLinks compact />
            <button onClick={() => setView(v => v === 'graph' ? 'workspace' : 'graph')} className="button px-3 py-1 text-xs flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> {view === 'graph' ? 'back to kit' : 'calibration graph'}
            </button>
            <button onClick={addRootNode} className="button button-primary px-3 py-1 text-xs flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> new root node <span className="opacity-60">(n)</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto w-full px-5 pt-7 pb-20 flex-1">
        {/* Context header */}
        <div className="text-sm text-ink-light mb-1">{kit.context}</div>
        <div className="flex items-baseline gap-3">
          <div className="text-2xl tracking-tight">{kit.name}</div>
          <div className="text-xs text-ink-muted">{kit.horizon}</div>
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <button onClick={addRootNode} className="button px-3 py-1 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add root observation or commitment</button>
          <button onClick={() => setView('graph')} className="button px-3 py-1 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Open full calibration graph</button>
          <button onClick={() => { const art = { kit, nodes, clarity, graph }; setForgeFor(art); setForgeTitle(`Full kit — ${kit.name}`) }} className="button px-3 py-1 flex items-center gap-1.5 border-accent/50"><Lock className="w-3.5 h-3.5" /> Forge whole-kit snapshot (careful)</button>
          <button onClick={loadDemo} className="button px-3 py-1 flex items-center gap-1.5 text-ink-muted">reload demo narrative</button>
        </div>

        {/* MAIN VIEWS */}
        {view === 'workspace' && (
          <>
            {/* The Loom — hierarchical nodes */}
            <div className="mt-7">
              <div className="uppercase text-[10px] tracking-[1px] text-ink-muted mb-2 flex items-center gap-2">
                ATTENTION LOOM — decomposed commitments
                <span className="normal-case text-ink-light">({nodes.length} nodes • {clarity.length} clarity cards)</span>
              </div>

              {tree.length === 0 && (
                <div className="card p-6 text-sm text-ink-light">
                  No nodes yet. Add a root observation (or hit “n”). Then decompose it into smaller, observable pieces. 
                  Every confidence number and burden rating you set becomes part of the calibration graph.
                </div>
              )}

              <div>
                {tree.map(root => (
                  <NodeCard 
                    key={root.id} 
                    node={root} 
                    onUpdate={updateNode} 
                    onDecompose={openDecompose} 
                    onAbandon={openAbandon} 
                    onProbe={openProbe} 
                    onForge={(n) => { setForgeFor(n); setForgeTitle(`Node: ${n.description.slice(0, 52)}`) }} 
                    onLogObservation={(n) => {
                      const obs = prompt('Quick observation (will be attached as a child node):')
                      if (obs && kit) api.createNode(kit.id, { description: obs, parent_id: n.id, confidence: 70, created_by: pid }).then(newN => { setNodes(p => [...p, newN]); refreshData(kit.id) })
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Clarity cards (recovery) */}
            {clarity.length > 0 && (
              <div className="mt-9">
                <div className="uppercase text-[10px] tracking-[1px] text-ink-muted mb-2">RECOVERY CLARITY — what we now know because something broke</div>
                <div className="space-y-2">
                  {clarity.map(c => (
                    <div key={c.id} className="card p-3.5 text-sm flex gap-4">
                      <div className="flex-1">
                        <div className="text-ink-light text-xs mb-0.5">{format(new Date(c.created_at), 'HH:mm')}</div>
                        <div><span className="text-ink-muted">Situation:</span> {c.situation}</div>
                        <div className="mt-1"><span className="text-ink-muted">Became clear:</span> {c.what_became_clear}</div>
                        {c.assumption_falsified && <div className="mt-1 text-[13px]"><span className="text-ink-muted">False assumption:</span> {c.assumption_falsified}</div>}
                      </div>
                      <div>
                        <button onClick={() => { setForgeFor(c); setForgeTitle(`Clarity: ${c.what_became_clear.slice(0, 48)}`) }} className="button text-xs px-2.5 py-1 flex items-center gap-1"><Share2 className="w-3 h-3" /> forge</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* THE GRAPH VIEW — the actual desired artifact */}
        {view === 'graph' && graph && (
          <div className="mt-3">
            <div className="uppercase text-[10px] tracking-[1px] text-ink-muted mb-2">CONFIDENCE CALIBRATION GRAPH</div>
            <div className="card p-5">
              <CalibrationGraph data={graph} onPointClick={(nid) => {
                const n = nodes.find(x => x.id === nid)
                if (n) { setView('workspace'); /* could scroll to it in real impl */ }
              }} />
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => { setForgeFor(graph); setForgeTitle(`Calibration — ${kit.name}`) }} className="button px-4 py-1.5 text-sm flex items-center gap-2"><Share2 className="w-4 h-4" /> Forge this graph as shareable artifact</button>
              <button onClick={() => setView('workspace')} className="button px-4 py-1.5 text-sm border">Back to the loom</button>
            </div>
            <div className="text-[11px] text-ink-muted mt-3 max-w-prose">Amber markers and rings indicate abandonment. The faint band shows typical drift between what groups report and what later proves true. Use this in after-action without naming blame.</div>
          </div>
        )}

        {/* Artifacts list (explicit shares made from this kit) */}
        {artifacts.length > 0 && (
          <div className="mt-10">
            <div className="uppercase text-[10px] tracking-[1px] text-ink-muted mb-2">EXPLICITLY FORGED ARTIFACTS FROM THIS KIT</div>
            <div className="text-xs text-ink-light mb-2">These are the only things that have left the private kit. Each was created by deliberate choice.</div>
            <div className="space-y-1.5">
              {artifacts.map(a => (
                <div key={a.id} className="text-sm flex items-center gap-3 card px-3 py-1.5">
                  <div className="flex-1 truncate">{a.title}</div>
                  <button onClick={async () => { const url = `${location.origin}/a/${a.share_code}`; await navigator.clipboard.writeText(url); toast.success('Link copied') }} className="button px-2 py-0.5 text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> copy link</button>
                  <div className="text-[10px] text-ink-muted font-mono">{a.share_code}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer with creator links */}
      <div className="border-t py-4 text-center text-xs">
        <CreatorLinks />
      </div>

      {/* Decompose modal */}
      <AnimatePresence>
        {decomposeFor && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setDecomposeFor(null)}>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-lg rounded border p-5" onClick={e => e.stopPropagation()}>
              <div className="font-medium">Decompose under: <span className="text-ink-light">{decomposeFor.description}</span></div>
              <textarea className="textarea w-full h-20 mt-3 p-3 text-sm" placeholder="Optional extra context for the AI (or just hit suggest)" value={decomposeText} onChange={e => setDecomposeText(e.target.value)} />
              <div className="mt-3 flex gap-2">
                <button onClick={runDecompose} className="button button-primary px-4 py-1.5 text-sm flex-1">Suggest concrete micro-tasks</button>
                <button onClick={() => { setDecomposeFor(null); setAiSuggestions([]) }} className="button px-4 py-1.5 text-sm border">Close</button>
              </div>

              {aiSuggestions.length > 0 && (
                <div className="mt-4 space-y-2">
                  {aiSuggestions.map((s, i) => (
                    <div key={i} onClick={() => acceptSuggestion(s)} className="cursor-pointer border p-3 rounded text-sm hover:border-accent">
                      <div>{s.description}</div>
                      <div className="text-[11px] text-ink-muted mt-1">starter confidence {s.starterConfidence}% • burden {s.burden} • {s.basis}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-ink-muted mt-3">Suggestions are grounded in the current situation + (when available) consented clarifications from other groups. You remain responsible for every number.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Probe modal */}
      <AnimatePresence>
        {probeFor && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setProbeFor(null)}>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-md rounded border p-5" onClick={e => e.stopPropagation()}>
              <div className="font-medium">Epistemic probes for this micro-task</div>
              <div className="text-xs text-ink-muted mt-0.5">Designed to surface unknown unknowns and calibration drift. {variant ? `Style: ${variant.probing_style}` : ''}</div>
              <div className="mt-4 space-y-2 text-sm">
                {probes.map((p, i) => <div key={i} className="border-l-2 border-accent pl-3 py-0.5">{p}</div>)}
              </div>
              <button onClick={() => setProbeFor(null)} className="mt-5 button px-4 py-1 text-sm w-full">Close</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Abandon + Clarity modal (the ethical core) */}
      <AbandonClarityModal 
        node={abandonFor} 
        open={!!abandonFor} 
        onClose={() => setAbandonFor(null)} 
        onConfirm={confirmAbandon} 
        participantId={pid}
        variant={variant || getLocalVariant()}
      />

      {/* Forge artifact modal (explicit share gate) */}
      <AnimatePresence>
        {forgeFor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4" onClick={() => setForgeFor(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} className="bg-white w-full max-w-md rounded border p-6" onClick={e => e.stopPropagation()}>
              <div className="font-medium text-lg">Forge shareable artifact</div>
              <div className="text-sm text-ink-muted mt-1">This is the only way anything leaves this private kit. The recipient will see only the snapshot you choose. You can revoke later.</div>

              <input className="input w-full mt-4 p-2 text-sm" value={forgeTitle} onChange={e => setForgeTitle(e.target.value)} placeholder="Title for the shared artifact" />

              <div className="mt-5 flex gap-2">
                <button onClick={() => forgeArtifact(forgeFor, forgeFor.artifact_type || (forgeFor.what_became_clear ? 'clarity_card' : forgeFor.points ? 'graph_slice' : 'kit_summary'))} className="button button-primary flex-1 py-2 text-sm">Forge &amp; copy link</button>
                <button onClick={() => setForgeFor(null)} className="button px-5 py-2 text-sm border">Cancel</button>
              </div>
              <div className="text-[11px] mt-3 text-ink-muted">The link is unguessable. Only people you give it to can see it. No one else in the group, no search, no index.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors closeButton />
    </div>
  )
}
